import { useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { X } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import type { AttentionItem, OverviewMonth, OverviewResponse, WaitingOn } from '../lib/types';
import { formatCount, formatCurrency, formatMonth, formatPercent } from '../lib/format';
import {
  POPOVER_ROWS,
  WAITING_ON_LABEL,
  changeFrom,
  daysSince,
  figureCsv,
  figureDetail,
  groupAttentionByOrg,
  type FigureDetail,
  type FigureKey,
} from '../lib/businessHealth';
import {
  ErrorState,
  LoadingState,
  MonthsSelect,
  MonthTextPicker,
  PageHeader,
  Section,
  StatCard,
  TableScroll,
  useDismiss,
  useInsightsQuery,
} from '../components/ui';

const CHART_TICK = { fontSize: 11, fill: '#a8a29e' };
const CHART_TOOLTIP = {
  borderRadius: 8,
  border: '1px solid #e7e5e4',
  fontSize: 12,
};
/** How much the running month fades, so it reads as not final. */
const PARTIAL_OPACITY = 0.4;

/** Axis labels: $750, $2.5k, $10k. */
function axisDollars(value: number): string {
  return Math.abs(value) >= 1000 ? `$${value / 1000}k` : `$${value}`;
}

function signed(value: number): string {
  if (value === 0) return formatCurrency(0);
  return `${value > 0 ? '+' : '−'}${formatCurrency(Math.abs(value))}`;
}

function changeHint(change: number | null): string {
  if (change === null) return 'no prior month';
  return `${change >= 0 ? '+' : '−'}${formatPercent(Math.abs(change))} vs last month`;
}

function downloadCsv(detail: FigureDetail, month: string) {
  const blob = new Blob([figureCsv(detail)], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${detail.title.toLowerCase().replace(/\s+/g, '-')}-${month}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/** What a figure is made of: the calculation, its source, and the companies behind it. */
function FigurePanel({ detail, month, onClose }: { detail: FigureDetail; month: string; onClose: () => void }) {
  const shown = detail.rows.slice(0, POPOVER_ROWS);
  const rest = detail.rows.slice(POPOVER_ROWS);
  const restTotal = rest.reduce((sum, r) => sum + r.amount, 0);

  return (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-medium uppercase tracking-wider text-stone-400">
            {detail.title} · {formatMonth(month)}
          </div>
          <div className="tabular mt-1 text-xl font-bold text-stone-900">{detail.total}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-4 text-[11px] font-medium uppercase tracking-wider text-stone-400">How it's calculated</div>
      <div className="mt-1">
        {detail.lines.map((line) => (
          <div
            key={line.label}
            className={`flex items-baseline justify-between gap-3 py-1.5 text-sm ${
              line.total ? 'mt-1 border-t border-stone-200 pt-2 font-semibold text-stone-900' : 'text-stone-600'
            }`}
          >
            <span>{line.label}</span>
            <span className="tabular shrink-0">{line.value}</span>
          </div>
        ))}
      </div>

      <div className="mt-3 text-[11px] font-medium uppercase tracking-wider text-stone-400">
        Where the numbers come from
      </div>
      <p className="mt-1 text-xs leading-relaxed text-stone-600">{detail.source}</p>
      {detail.ties && <p className="mt-1 text-xs font-medium text-emerald-700">{detail.ties}</p>}

      {detail.rows.length > 0 && (
        <>
          <div className="mt-3 text-[11px] font-medium uppercase tracking-wider text-stone-400">{detail.rowsTitle}</div>
          <div className="mt-1">
            {shown.map((row) => (
              <div
                key={row.name}
                className="flex items-baseline justify-between gap-3 border-b border-stone-100 py-1.5 text-sm"
              >
                <span className="min-w-0 truncate font-medium text-stone-800">{row.name}</span>
                <span className="ml-auto truncate text-xs text-stone-400">{row.note}</span>
                <span className="tabular shrink-0 text-stone-700">{row.value}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-center justify-between gap-3 text-xs">
            <span className="text-stone-400">
              {rest.length > 0 ? `${formatCount(rest.length)} more · ${formatCurrency(restTotal)}` : ''}
            </span>
            <button
              type="button"
              onClick={() => downloadCsv(detail, month)}
              className="font-medium text-primary hover:underline"
            >
              Download all rows (CSV)
            </button>
          </div>
        </>
      )}
    </>
  );
}

/**
 * A headline number that opens what it is made of. One is open at a time; it
 * closes on outside click or Escape.
 */
function Figure({
  figure,
  open,
  onToggle,
  onClose,
  data,
  summary,
  align = 'left',
  children,
}: {
  figure: FigureKey;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  data: OverviewResponse;
  summary: OverviewMonth;
  align?: 'left' | 'right';
  children: ReactNode;
}) {
  const rootRef = useRef<HTMLSpanElement>(null);
  const panelId = useId();
  const detail = useMemo(() => (open ? figureDetail(data, summary, figure) : null), [open, data, summary, figure]);

  useDismiss(rootRef, open, onClose);

  return (
    <span ref={rootRef} className="relative inline-flex">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
        className={`tabular rounded-sm underline decoration-dotted decoration-stone-300 underline-offset-4 transition-colors hover:text-primary hover:decoration-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 ${
          open ? 'text-primary decoration-primary decoration-solid' : ''
        }`}
      >
        {children}
      </button>
      {open && detail && (
        <div
          id={panelId}
          role="dialog"
          aria-label={detail.title}
          className={`absolute top-full z-40 mt-2 w-[26rem] max-w-[calc(100vw-2rem)] rounded-xl border border-stone-200 bg-white p-4 text-left font-normal shadow-hero ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          <FigurePanel detail={detail} month={summary.month} onClose={onClose} />
        </div>
      )}
    </span>
  );
}

const WAITING_ON_TONE: Record<WaitingOn, string> = {
  customer: 'bg-orange-100 text-orange-800',
  justifi: 'bg-stone-100 text-stone-700',
  telnyx: 'bg-stone-100 text-stone-700',
  us: 'bg-red-100 text-red-800',
};

/** How many stuck customers the home page lists before "show all". */
const ATTENTION_ROWS = 10;

function NeedsAttention({ items }: { items: AttentionItem[] }) {
  const now = new Date();
  const [showAll, setShowAll] = useState(false);
  // One row per organization, with everything stuck for it together.
  const groups = groupAttentionByOrg(items);
  const shown = showAll ? groups : groups.slice(0, ATTENTION_ROWS);
  // Organizations, not items: one that waits on two things counts once for each.
  const counts = groups.reduce<Record<WaitingOn, number>>(
    (acc, group) => {
      for (const who of new Set(group.items.map((i) => i.waitingOn))) acc[who] += 1;
      return acc;
    },
    { customer: 0, justifi: 0, telnyx: 0, us: 0 },
  );

  return (
    <Section title="Needs attention" subtitle="Organizations that are stuck, and who they are waiting on — oldest first">
      <div className="mb-3 flex flex-wrap gap-2 text-xs">
        {(Object.keys(counts) as WaitingOn[])
          .filter((key) => counts[key] > 0)
          .map((key) => (
            <span key={key} className={`rounded-full px-2.5 py-0.5 font-medium ${WAITING_ON_TONE[key]}`}>
              {formatCount(counts[key])} waiting on {WAITING_ON_LABEL[key].toLowerCase()}
            </span>
          ))}
      </div>
      {groups.length === 0 ? (
        <p className="py-6 text-center text-sm text-stone-400">Nobody is stuck.</p>
      ) : (
        <TableScroll>
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b border-stone-200 text-left text-[11px] uppercase tracking-wider text-stone-400">
                <th className="py-2.5 font-medium">Organization</th>
                <th className="py-2.5 pr-4 font-medium" colSpan={3}>
                  <div className="grid grid-cols-[minmax(0,1fr)_9rem_6rem] gap-x-4">
                    <span>What is stuck</span>
                    <span>Waiting on</span>
                    <span className="text-right">Stuck for</span>
                  </div>
                </th>
                <th className="py-2.5" />
              </tr>
            </thead>
            <tbody>
              {shown.map((group) => (
                <tr key={group.orgId} className="border-b border-stone-100 align-top">
                  <td className="whitespace-nowrap py-2.5 pr-4 font-medium text-stone-900">{group.name}</td>
                  <td className="py-2.5 pr-4" colSpan={3}>
                    <div className="space-y-2.5">
                      {group.items.map((item) => {
                        const days = daysSince(item.since, now);
                        return (
                          <div key={item.kind} className="grid grid-cols-[minmax(0,1fr)_9rem_6rem] items-start gap-x-4">
                            <div>
                              <div className="font-medium text-stone-800">{item.title}</div>
                              {item.detail && <div className="mt-0.5 text-xs text-stone-500">{item.detail}</div>}
                            </div>
                            <span
                              className={`justify-self-start whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${WAITING_ON_TONE[item.waitingOn]}`}
                            >
                              {WAITING_ON_LABEL[item.waitingOn]}
                            </span>
                            <span className="tabular whitespace-nowrap text-right text-stone-700">
                              {days === null ? '—' : `${formatCount(days)} ${days === 1 ? 'day' : 'days'}`}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </td>
                  <td className="whitespace-nowrap py-2.5 pl-4 text-right text-xs font-medium">
                    {group.items.some((i) => i.waitingOn === 'customer') && group.contactEmail && (
                      <a href={`mailto:${group.contactEmail}`} className="mr-3 text-primary hover:underline">
                        Email the owner
                      </a>
                    )}
                    <Link to={`/organizations/${encodeURIComponent(group.orgId)}`} className="text-primary hover:underline">
                      Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {groups.length > ATTENTION_ROWS && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="mt-3 text-xs font-medium text-primary hover:underline"
            >
              {showAll ? `Show the ${ATTENTION_ROWS} oldest` : `Show all ${formatCount(groups.length)} organizations`}
            </button>
          )}
        </TableScroll>
      )}
    </Section>
  );
}

/**
 * Admin Insights home — the state of the business (RUT-4071).
 *
 * Revenue to Ruta   = plan revenue (invoices) + Ruta Pay spread
 * Subscription margin = revenue − direct costs (what counted companies cause)
 * Overall margin      = subscription margin − shared costs
 */
export function OverviewPage() {
  const [months, setMonths] = useState(6);
  /** null = follow the latest month in the loaded window. */
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  /** Which figure's popover is open — keyed by where it sits, since a figure can appear twice. */
  const [openFigure, setOpenFigure] = useState<string | null>(null);
  const { data, isLoading, error } = useInsightsQuery<OverviewResponse>(
    ['overview', months],
    `/costs/platform?months=${months}`,
  );

  if (isLoading) return <LoadingState />;
  if (error || !data) return <ErrorState error={error} />;

  const availableMonths = data.byMonth.map((m) => m.month);
  const latestMonth = availableMonths[availableMonths.length - 1];
  const activeMonth = selectedMonth && availableMonths.includes(selectedMonth) ? selectedMonth : latestMonth;
  const index = data.byMonth.findIndex((m) => m.month === activeMonth);
  const summary = data.byMonth[index];
  const prior = index > 0 ? data.byMonth[index - 1] : null;
  const isPartial = summary.month === data.partialMonth;
  const monthLabel = formatMonth(summary.month);

  const figure = (where: 'tile' | 'margin', key: FigureKey, children: ReactNode, align: 'left' | 'right' = 'left') => {
    const id = `${where}-${key}`;
    return (
      <Figure
        figure={key}
        open={openFigure === id}
        onToggle={() => setOpenFigure((current) => (current === id ? null : id))}
        onClose={() => setOpenFigure(null)}
        data={data}
        summary={summary}
        align={align}
      >
        {children}
      </Figure>
    );
  };

  const chartData = data.byMonth.map((m) => ({
    month: formatMonth(m.month),
    partial: m.month === data.partialMonth,
    Plans: m.planRevenue,
    'Ruta Pay spread': m.rutaPaySpread,
    'Direct costs': m.directCost.total,
    'Shared costs': m.sharedCost.total,
  }));

  const movement = summary.movement;
  // Counted companies that took card payments this month — not today's state.
  const onRutaPay = data.byOrg.filter((org) => {
    const m = org.byMonth.find((x) => x.month === summary.month);
    return m?.counted && m.cardVolume > 0;
  }).length;
  const collectedShare = summary.billing.invoiced > 0 ? summary.billing.collected / summary.billing.invoiced : null;

  const marginLine = (label: string, hint: string | null, value: ReactNode, total = false) => (
    <div
      className={`flex items-baseline justify-between gap-3 py-2 text-sm ${
        total
          ? 'border-t border-stone-200 pt-2.5 font-semibold text-stone-900'
          : 'border-b border-stone-100 text-stone-600'
      }`}
    >
      <span>
        {label}
        {hint && <span className="ml-1.5 text-xs text-stone-400">{hint}</span>}
      </span>
      <span className="tabular shrink-0">{value}</span>
    </div>
  );

  return (
    <>
      <PageHeader
        title="State of the business"
        subtitle={
          isPartial
            ? `${monthLabel} so far — not final until the month ends`
            : `${monthLabel}, compared with the month before`
        }
        actions={
          <MonthTextPicker
            months={availableMonths}
            value={activeMonth}
            onChange={setSelectedMonth}
            label={activeMonth === latestMonth ? 'Latest month' : 'Summary month'}
          />
        }
      />

      <p className="-mt-3 mb-4 text-xs text-stone-500">Click any underlined figure to see what it is made of.</p>

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatCard
          label="Plan revenue"
          value={figure('tile', 'plan', formatCurrency(summary.planRevenue))}
          hint={`${
            // A running month is not comparable with a whole one — companies bill on their own day.
            isPartial
              ? 'so far this month'
              : changeHint(prior ? changeFrom(prior.planRevenue, summary.planRevenue) : null)
          } · ${collectedShare === null ? 'nothing invoiced' : `${formatPercent(collectedShare, 0)} collected`}${
            summary.failedCharges ? ` · ${formatCount(summary.failedCharges)} declined` : ''
          }`}
        />
        <StatCard
          label="Ruta Pay spread"
          value={figure('tile', 'spread', formatCurrency(summary.rutaPaySpread))}
          hint={`kept on ${formatCurrency(summary.rutaPay.volume)} of card payments`}
        />
        <StatCard
          label="Organizations this month"
          value={formatCount(summary.countedOrgs)}
          hint={`+${formatCount(movement.newCount)} new · ${
            isPartial
              ? `${formatCount(movement.notYetBilledCount)} not billed yet`
              : `${formatCount(movement.lostCount)} not billed`
          } · ${formatCount(onRutaPay)} on Ruta Pay`}
        />
        <StatCard
          label="Subscription margin"
          value={figure('tile', 'subscription', formatPercent(summary.subscriptionMarginPct, 0))}
          hint={`${formatCurrency(summary.subscriptionMargin)} before shared costs${isPartial ? ' · so far' : ''}`}
        />
        <StatCard
          label="Overall margin"
          value={figure('tile', 'overall', formatPercent(summary.overallMarginPct, 0), 'right')}
          hint={`${formatCurrency(summary.overallMargin)} after shared costs${isPartial ? ' · so far' : ''}`}
        />
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-5">
        <section className="rounded-xl border border-stone-200 bg-white p-4 shadow-card sm:p-5 xl:col-span-3">
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-sm font-bold text-stone-900">Revenue to Ruta by month</h2>
              <p className="mt-0.5 text-xs text-stone-500">
                Invoiced plan revenue plus the Ruta Pay spread. The faded month is still running.
              </p>
            </div>
            <MonthsSelect value={months} onChange={setMonths} variant="light" label="Show" />
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" vertical={false} />
              <XAxis dataKey="month" tick={CHART_TICK} axisLine={false} tickLine={false} />
              <YAxis tick={CHART_TICK} axisLine={false} tickLine={false} tickFormatter={axisDollars} width={56} />
              <Tooltip contentStyle={CHART_TOOLTIP} formatter={(value: number) => formatCurrency(value)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Plans" stackId="r" fill="#0a1f0a">
                {chartData.map((d) => (
                  <Cell key={d.month} fillOpacity={d.partial ? PARTIAL_OPACITY : 1} />
                ))}
              </Bar>
              <Bar dataKey="Ruta Pay spread" stackId="r" fill="#8fa88f" radius={[3, 3, 0, 0]}>
                {chartData.map((d) => (
                  <Cell key={d.month} fillOpacity={d.partial ? PARTIAL_OPACITY : 1} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          <div className="mt-4 border-t border-stone-100 pt-4">
            <div className="text-[11px] font-medium uppercase tracking-wider text-stone-400">
              How plan revenue moved in {monthLabel}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                {
                  value: movement.newAmount,
                  label: `${formatCount(movement.newCount)} new companies`,
                },
                {
                  value: movement.upAmount,
                  label: `${formatCount(movement.upCount)} billed more`,
                },
                isPartial
                  ? {
                      // Not a loss: their billing day has not come round yet.
                      value: 0,
                      label: `${formatCount(movement.notYetBilledCount)} not billed yet (${formatCurrency(
                        movement.notYetBilledAmount,
                      )} last month)`,
                    }
                  : {
                      value: movement.lostAmount,
                      // No invoice this month: gone, or a declined renewal — a decline records nothing.
                      label: `${formatCount(movement.lostCount)} not billed this month`,
                    },
                {
                  value: movement.downAmount,
                  label: `${formatCount(movement.downCount)} billed less`,
                },
              ].map((part) => (
                <div key={part.label}>
                  <div
                    className={`tabular text-base font-semibold ${
                      part.value > 0 ? 'text-emerald-700' : part.value < 0 ? 'text-red-700' : 'text-stone-500'
                    }`}
                  >
                    {signed(part.value)}
                  </div>
                  <div className="text-xs text-stone-500">{part.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-stone-200 bg-white p-4 shadow-card sm:p-5 xl:col-span-2">
          <h2 className="text-sm font-bold text-stone-900">Margin, two ways</h2>
          <p className="mt-0.5 text-xs text-stone-500">
            Subscription margin leaves out shared costs. Overall margin takes them off.
          </p>
          <div className="mt-3">
            {marginLine(
              'Plan revenue',
              'invoiced',
              figure('margin', 'plan', formatCurrency(summary.planRevenue), 'right'),
            )}
            {marginLine(
              'Ruta Pay spread',
              null,
              figure('margin', 'spread', formatCurrency(summary.rutaPaySpread), 'right'),
            )}
            {marginLine(
              'Direct costs',
              'texts, visits, AI, crew GPS',
              figure('margin', 'direct', `− ${formatCurrency(summary.directCost.total)}`, 'right'),
            )}
            {marginLine(
              'Subscription margin',
              null,
              figure(
                'margin',
                'subscription',
                `${formatCurrency(summary.subscriptionMargin)} · ${formatPercent(summary.subscriptionMarginPct, 0)}`,
                'right',
              ),
              true,
            )}
            <div className="h-2" />
            {marginLine(
              'Shared costs',
              'always-on, vendors, texting setup',
              figure('margin', 'shared', `− ${formatCurrency(summary.sharedCost.total)}`, 'right'),
            )}
            {marginLine(
              'Overall margin',
              null,
              figure(
                'margin',
                'overall',
                `${formatCurrency(summary.overallMargin)} · ${formatPercent(summary.overallMarginPct, 0)}`,
                'right',
              ),
              true,
            )}
          </div>
          <p className="mt-4 rounded-lg border border-stone-100 bg-stone-50 px-3 py-2 text-xs leading-relaxed text-stone-600">
            JustiFi's processing fees are neither our revenue nor our cost. Ruta Pay counts only as the spread we keep. Plan revenue includes invoices still open.
          </p>
        </section>
      </div>

      <NeedsAttention items={data.attention} />

      <Section title="Cost by month" subtitle="Direct costs the companies we bill cause, and shared costs behind everyone">
        <p className="-mt-2 mb-3 text-xs text-stone-500">
          Every line, unit cost and vendor limit is on{' '}
          <Link to="/costs" className="text-primary hover:underline">
            Costs
          </Link>
          .
        </p>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" vertical={false} />
            <XAxis dataKey="month" tick={CHART_TICK} axisLine={false} tickLine={false} />
            <YAxis tick={CHART_TICK} axisLine={false} tickLine={false} tickFormatter={axisDollars} width={56} />
            <Tooltip contentStyle={CHART_TOOLTIP} formatter={(value: number) => formatCurrency(value)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Direct costs" stackId="c" fill="#c2410c">
              {chartData.map((d) => (
                <Cell key={d.month} fillOpacity={d.partial ? PARTIAL_OPACITY : 1} />
              ))}
            </Bar>
            <Bar dataKey="Shared costs" stackId="c" fill="#a8a29e" radius={[3, 3, 0, 0]}>
              {chartData.map((d) => (
                <Cell key={d.month} fillOpacity={d.partial ? PARTIAL_OPACITY : 1} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Section>
    </>
  );
}
